import MuleBadge from '../../components/MuleBadge';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import userSettingsService from '../../services/userSettings.service';
import DriverPreferenceModal from '../../components/DriverPreferenceModal';
import ParkingLocationModal, { getParkingColorStyles } from '../../components/ParkingLocationModal';
import TagBadge from '../../components/TagBadge';
import { Tooltip } from '../../components/ui/Tooltip';
import InfoModal from '../../components/common/InfoModal';
import DateTimePicker from '../../components/DateTimePicker';
import ThreeStateToggle, { ThreeStateValue } from '../../components/ThreeStateToggle';
import MultiSelect from '../../components/MultiSelect';
import Toggle from '../../components/Toggle';
import DriverPhoneLink from '../../components/DriverPhoneLink';

interface LocationPreference {
  location_id: number;
  location_name: string;
  location_type?: string;
  location_address1?: string;
  location_address2?: string;
  location_city?: string;
  location_state?: string;
  location_zip?: string;
  preference_type: 'preferred' | 'not_preferred' | 'banned';
  ban_start_date?: string;
  ban_end_date?: string;
}

interface DriverPreferences {
  driver: string;
  driver_name: string;
  isMule?: boolean;
  preference: string;  // OTR or LOCAL
  parkingloc: number | null;
  parking_location_name: string;
  parking_color: number | null;
  region: string;
  start_time: string | null;
  start_time_qualifier: 'before' | 'at' | 'after';
  live_pref: ThreeStateValue;
  ppe: 'yes' | 'no';
  pre_pull: 'yes' | 'no';
  geo_autostamp_arrival: 'yes' | 'no';
  geo_autostamp_depart: 'yes' | 'no';
  preferred: LocationPreference[];
  not_preferred: LocationPreference[];
  banned: LocationPreference[];
  textmsgnumber?: string;
  homephone?: string;
}

interface DispatcherGroup {
  dispatcher: string;
  region: string;
  drivers: DriverPreferences[];
}

interface DriverPreferencesBansProps {
  selectedDispatchers: string[];
  selectedRegions: string[];
  refreshTrigger: number;
  usernameToDisplayname: Map<string, string>;
}

// Convert name to title case (camel case)
const toTitleCase = (str: string | undefined) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format time from 24-hour (HH:MM) to 12-hour with AM/PM
const formatTime12Hour = (time: string | null) => {
  if (!time) return '';
  const [hoursStr, minutesStr] = time.split(':');
  const hours = parseInt(hoursStr);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutesStr} ${period}`;
};

// Get effective default start time for a driver based on their region
const getEffectiveDefaultStartTime = (
  region: string | null,
  config: { global: string; regions: Record<string, string> }
): string => {
  if (region && config.regions[region]) {
    return config.regions[region];
  }
  return config.global;
};

const getEffectiveDefaultParkingLocation = (
  region: string | null,
  config: { global: { id: string; name: string } | null; regions: Record<string, { id: string; name: string }> }
): { id: string; name: string } | null => {
  if (region && config.regions[region]) {
    return config.regions[region];
  }
  return config.global;
};

// Sort drivers within a group by saved order, with unsaved drivers at the end
const sortDriversByOrder = (
  drivers: DriverPreferences[],
  savedOrder: string[] | undefined
): DriverPreferences[] => {
  if (!savedOrder || savedOrder.length === 0) {
    return drivers;
  }

  const orderMap = new Map(savedOrder.map((id, index) => [id, index]));

  return [...drivers].sort((a, b) => {
    const aIndex = orderMap.get(a.driver);
    const bIndex = orderMap.get(b.driver);

    // Both have saved positions - sort by saved order
    if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
    }
    // Only a has saved position - a comes first
    if (aIndex !== undefined) return -1;
    // Only b has saved position - b comes first
    if (bIndex !== undefined) return 1;
    // Neither has saved position - maintain original order
    return 0;
  });
};

const DriverPreferencesBans: React.FC<DriverPreferencesBansProps> = ({
  selectedDispatchers,
  selectedRegions,
  refreshTrigger,
  usernameToDisplayname,
}) => {
  const [data, setData] = useState<DispatcherGroup[]>([]);
  const [defaultStartTimeConfig, setDefaultStartTimeConfig] = useState<{
    global: string;
    regions: Record<string, string>;
  }>({ global: '06:00', regions: {} });
  const [defaultParkingLocationConfig, setDefaultParkingLocationConfig] = useState<{
    global: { id: string; name: string } | null;
    regions: Record<string, { id: string; name: string }>;
  }>({ global: null, regions: {} });
  const [loading, setLoading] = useState(true);
  const [expandedDispatchers, setExpandedDispatchers] = useState<Set<string>>(new Set());
  // Ref, NOT state: as state (and a dep of the fetch effect) the flag's flip
  // after the first load re-ran the effect, so every page load fetched twice.
  const initialLoadCompleteRef = useRef(false);
  const [columnSearches, setColumnSearches] = useState({
    driver: '',
    startTime: '',
    preferred: '',
    notPreferred: '',
    banned: ''
  });
  const [livePrefFilter, setLivePrefFilter] = useState<string[]>([]);
  const [ppeFilter, setPpeFilter] = useState<string[]>([]);
  const [prePullFilter, setPrePullFilter] = useState<string[]>([]);
  const [autoArrivalFilter, setAutoArrivalFilter] = useState<string[]>([]);
  const [autoDepartFilter, setAutoDepartFilter] = useState<string[]>([]);

  // Driver sort order (from Drivers page drag-and-drop)
  const [driverSortOrder, setDriverSortOrder] = useState<Record<string, string[]>>({});

  // Load driver sort order from user settings
  useEffect(() => {
    const loadSortOrder = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('drivers_sort_order');
        if (setting?.setting_value) {
          setDriverSortOrder(setting.setting_value);
        }
      } catch (error) {
        console.error('Failed to load driver sort order:', error);
      }
    };
    loadSortOrder();
  }, []);

  // Live Loads filter options
  const livePrefOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'neutral', label: 'No Pref' },
  ];

  // PPE and Pre-Pull filter options
  const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ];

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDriver, setModalDriver] = useState('');
  const [modalDriverName, setModalDriverName] = useState('');
  const [modalPreferenceType, setModalPreferenceType] = useState<'preferred' | 'not_preferred' | 'banned'>('preferred');
  const [editingBan, setEditingBan] = useState<LocationPreference | null>(null);

  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ driver: string; locationId: number; preferenceType: string } | null>(null);

  // Start time modal state
  const [startTimeModalOpen, setStartTimeModalOpen] = useState(false);
  const [startTimeDriver, setStartTimeDriver] = useState('');
  const [startTimeDriverName, setStartTimeDriverName] = useState('');
  const [startTimeValue, setStartTimeValue] = useState('');
  const [startTimeQualifier, setStartTimeQualifier] = useState<'before' | 'at' | 'after'>('at');
  const [savingStartTime, setSavingStartTime] = useState(false);

  // Track items being deleted
  const [deletingStartTime, setDeletingStartTime] = useState<Set<string>>(new Set());
  const [deletingPreferences, setDeletingPreferences] = useState<Set<string>>(new Set());

  // Parking location modal state
  const [parkingModalOpen, setParkingModalOpen] = useState(false);
  const [parkingModalDriver, setParkingModalDriver] = useState('');
  const [parkingModalDriverName, setParkingModalDriverName] = useState('');
  const [parkingModalCurrentLoc, setParkingModalCurrentLoc] = useState<number | null>(null);
  const [parkingModalCurrentName, setParkingModalCurrentName] = useState('');
  const [parkingModalCurrentColor, setParkingModalCurrentColor] = useState<number | null>(null);

  const toggleDispatcher = (dispatcher: string) => {
    setExpandedDispatchers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dispatcher)) {
        newSet.delete(dispatcher);
      } else {
        newSet.add(dispatcher);
      }
      return newSet;
    });
  };

  const openPreferenceModal = (driver: string, driverName: string, type: 'preferred' | 'not_preferred' | 'banned') => {
    setModalDriver(driver);
    setModalDriverName(driverName);
    setModalPreferenceType(type);
    setModalOpen(true);
  };

  const openParkingModal = (driver: DriverPreferences) => {
    setParkingModalDriver(driver.driver);
    setParkingModalDriverName(driver.driver_name);
    setParkingModalCurrentLoc(driver.parkingloc);
    setParkingModalCurrentName(driver.parking_location_name);
    setParkingModalCurrentColor(driver.parking_color);
    setParkingModalOpen(true);
  };

  const handleParkingLocationSave = async () => {
    try {
      const response = await api.get('/safety/driver-preferences');
      const responseData = response.data;
      if (responseData?.groups) {
        setData(responseData.groups || []);
      } else {
        setData(responseData || []);
      }
    } catch (error) {
      console.error('Error refreshing driver preferences:', error);
    }
  };

  const handleSaveComplete = async () => {
    // Refresh data
    try {
      const response = await api.get('/safety/driver-preferences');
      const responseData = response.data;
      if (responseData?.groups) {
        setData(responseData.groups || []);
      } else {
        setData(responseData || []);
      }
    } catch (error) {
      console.error('Error refreshing driver preferences:', error);
    }
  };

  const handleDeletePreference = (driver: string, locationId: number, preferenceType: string) => {
    setDeleteTarget({ driver, locationId, preferenceType });
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setConfirmOpen(false);

    // Optimistically remove from UI immediately
    setData(prevData => prevData.map(group => ({
      ...group,
      drivers: group.drivers.map(driver => {
        if (driver.driver === deleteTarget.driver) {
          return {
            ...driver,
            preferred: deleteTarget.preferenceType === 'preferred'
              ? driver.preferred.filter(loc => loc.location_id !== deleteTarget.locationId)
              : driver.preferred,
            not_preferred: deleteTarget.preferenceType === 'not_preferred'
              ? driver.not_preferred.filter(loc => loc.location_id !== deleteTarget.locationId)
              : driver.not_preferred,
            banned: deleteTarget.preferenceType === 'banned'
              ? driver.banned.filter(loc => loc.location_id !== deleteTarget.locationId)
              : driver.banned,
          };
        }
        return driver;
      })
    })));

    // Process backend delete
    try {
      await api.delete(`/safety/driver-preferences/${deleteTarget.driver}/${deleteTarget.locationId}/${deleteTarget.preferenceType}`);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting preference:', error);
      // On error, refresh to restore correct state
      handleSaveComplete();
    }
  };

  const handleEditBan = (driver: string, driverName: string, location: LocationPreference, e: React.MouseEvent) => {
    // Check if click came from the remove button - if so, ignore
    const target = e.target as HTMLElement;
    if (target.closest('button[type="button"]')) {
      return;
    }

    e.stopPropagation(); // Prevent cell click from firing
    setModalDriver(driver);
    setModalDriverName(driverName);
    setModalPreferenceType('banned');
    setEditingBan(location);
    setModalOpen(true);
  };

  const openStartTimeModal = (driver: string, driverName: string, currentTime: string | null) => {
    setStartTimeDriver(driver);
    setStartTimeDriverName(driverName);
    // Convert time string (HH:MM:SS) to Date object for DateTimePicker
    if (currentTime) {
      const [hours, minutes] = currentTime.split(':');
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      setStartTimeValue(date.toTimeString().split(' ')[0].substring(0, 5)); // HH:MM format
    } else {
      setStartTimeValue('');
    }
    setStartTimeModalOpen(true);
  };

  const handleSaveStartTime = async () => {
    setSavingStartTime(true);
    try {
      await api.post('/safety/driver-settings/start-time', {
        driver: startTimeDriver,
        start_time: startTimeValue || null,
        start_time_qualifier: 'at' // Always use 'at' - qualifiers not needed
      });
      handleSaveComplete();
      setStartTimeModalOpen(false);
    } catch (error) {
      console.error('Error saving start time:', error);
    } finally {
      setSavingStartTime(false);
    }
  };

  const handleStartTimeChange = (value: Date | { date?: Date; timeQualifier?: 'before' | 'at' | 'after' } | null) => {
    if (value instanceof Date) {
      // Convert Date to HH:MM format
      const hours = value.getHours().toString().padStart(2, '0');
      const minutes = value.getMinutes().toString().padStart(2, '0');
      setStartTimeValue(`${hours}:${minutes}`);
    } else if (value && 'date' in value && value.date) {
      // Handle DateTimeValue with date property
      const hours = value.date.getHours().toString().padStart(2, '0');
      const minutes = value.date.getMinutes().toString().padStart(2, '0');
      setStartTimeValue(`${hours}:${minutes}`);
    } else {
      setStartTimeValue('');
    }
  };

  const formatBanPeriod = (ban: LocationPreference) => {
    if (!ban.ban_end_date) {
      return 'Permaban';
    }

    const end = new Date(ban.ban_end_date);
    return `${end.getMonth() + 1}/${end.getDate()}/${end.getFullYear()}`;
  };

  // Filter data based on selected dispatchers, regions, search, and column searches
  const filteredData = useMemo(() => {
    let filtered = data;

    if (selectedDispatchers.length > 0) {
      const selectedDisplaynames = new Set(
        selectedDispatchers.map(username => usernameToDisplayname.get(username) || username)
      );
      filtered = filtered.filter(group => selectedDisplaynames.has(group.dispatcher));
    }

    if (selectedRegions.length > 0 || columnSearches.driver || columnSearches.startTime || livePrefFilter.length > 0 || ppeFilter.length > 0 || prePullFilter.length > 0 || autoArrivalFilter.length > 0 || autoDepartFilter.length > 0 || columnSearches.preferred || columnSearches.notPreferred || columnSearches.banned) {
      filtered = filtered.map(group => ({
        ...group,
        drivers: group.drivers.filter(driver => {
          // Check driver column search
          if (columnSearches.driver) {
            const query = columnSearches.driver.toLowerCase();
            const matchesDriver = driver.driver.toLowerCase().includes(query);
            const matchesName = driver.driver_name?.toLowerCase().includes(query);
            const matchesParkingLoc = driver.parking_location_name?.toLowerCase().includes(query);
            if (!matchesDriver && !matchesName && !matchesParkingLoc) {
              return false;
            }
          }

          // Check start time filter
          if (columnSearches.startTime) {
            const query = columnSearches.startTime.toLowerCase().trim();
            const formattedTime = formatTime12Hour(driver.start_time).toLowerCase();

            // Simple text match
            if (!formattedTime.includes(query)) {
              return false;
            }
          }

          // Check live pref filter
          if (livePrefFilter.length > 0) {
            const driverLivePref = driver.live_pref || 'neutral';
            if (!livePrefFilter.includes(driverLivePref)) {
              return false;
            }
          }

          // Check PPE filter
          if (ppeFilter.length > 0) {
            const driverPpe = driver.ppe || 'no';
            if (!ppeFilter.includes(driverPpe)) {
              return false;
            }
          }

          // Check Pre-Pull filter
          if (prePullFilter.length > 0) {
            const driverPrePull = driver.pre_pull || 'no';
            if (!prePullFilter.includes(driverPrePull)) {
              return false;
            }
          }

          // Check geofence auto-stamp filters
          if (autoArrivalFilter.length > 0) {
            if (!autoArrivalFilter.includes(driver.geo_autostamp_arrival || 'no')) {
              return false;
            }
          }
          if (autoDepartFilter.length > 0) {
            if (!autoDepartFilter.includes(driver.geo_autostamp_depart || 'no')) {
              return false;
            }
          }

          // Check preferred column search
          if (columnSearches.preferred) {
            const query = columnSearches.preferred.toLowerCase();
            const hasMatch = driver.preferred.some(loc =>
              loc.location_name.toLowerCase().includes(query)
            );
            if (!hasMatch) {
              return false;
            }
          }

          // Check not-preferred column search
          if (columnSearches.notPreferred) {
            const query = columnSearches.notPreferred.toLowerCase();
            const hasMatch = driver.not_preferred.some(loc =>
              loc.location_name.toLowerCase().includes(query)
            );
            if (!hasMatch) {
              return false;
            }
          }

          // Check banned column search
          if (columnSearches.banned) {
            const query = columnSearches.banned.toLowerCase();
            const hasMatch = driver.banned.some(loc =>
              loc.location_name.toLowerCase().includes(query)
            );
            if (!hasMatch) {
              return false;
            }
          }

          // Check region filter
          if (selectedRegions.length > 0 && !selectedRegions.includes(driver.region)) {
            return false;
          }

          return true;
        })
      })).filter(group => group.drivers.length > 0);
    }

    // Apply saved sort order to drivers within each group
    return filtered.map(group => ({
      ...group,
      drivers: sortDriversByOrder(group.drivers, driverSortOrder[group.dispatcher])
    }));
  }, [data, selectedDispatchers, selectedRegions, columnSearches, livePrefFilter, ppeFilter, prePullFilter, autoArrivalFilter, autoDepartFilter, driverSortOrder, usernameToDisplayname]);

  // Expand all dispatchers by default when data loads
  useEffect(() => {
    if (data.length > 0) {
      setExpandedDispatchers(new Set(data.map(g => g.dispatcher)));
    }
  }, [data]);

  // Fetch data on component mount and when refresh triggered
  useEffect(() => {
    const fetchData = async () => {
      // Only show loading on initial load, not on background refresh
      const isBackgroundRefresh = initialLoadCompleteRef.current;

      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      try {
        const response = await api.get('/safety/driver-preferences');
        const responseData = response.data;
        // Handle new response structure with groups and config
        if (responseData?.groups) {
          setData(responseData.groups || []);
          if (responseData.default_start_time_config) {
            setDefaultStartTimeConfig(responseData.default_start_time_config);
          }
          if (responseData.default_parking_location_config) {
            setDefaultParkingLocationConfig(responseData.default_parking_location_config);
          }
        } else {
          // Fallback for old response format (just array)
          setData(responseData || []);
        }
        initialLoadCompleteRef.current = true;
      } catch (error) {
        console.error('Error fetching driver preferences:', error);
        if (!isBackgroundRefresh) {
          setData([]);
        }
      } finally {
        if (!isBackgroundRefresh) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [refreshTrigger]);

  return (
    <div className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg overflow-auto mb-16">
      <table className="w-full border-collapse table-fixed">
        {/* Header */}
        <thead className="bg-slate-600 dark:bg-slate-700 text-white sticky top-0 z-10">
          <tr>
            <th className="px-2 py-0.5 text-left text-xs font-semibold border-r border-slate-500 w-80">
              Driver
            </th>
            <th className="px-2 py-0.5 text-center text-xs font-semibold border-r border-slate-500 w-32">
              Start Time
            </th>
            <th className="px-2 py-0.5 text-center text-xs font-semibold border-r border-slate-500 w-40">
              Live Loads
            </th>
            <th className="px-2 py-0.5 text-center text-xs font-semibold border-r border-slate-500 w-24">
              PPE
            </th>
            <th className="px-2 py-0.5 text-center text-xs font-semibold border-r border-slate-500 w-24">
              Pre-Pull
            </th>
            <th className="px-2 py-0.5 text-center text-xs font-semibold border-r border-slate-500 w-24">
              Auto Arrival
            </th>
            <th className="px-2 py-0.5 text-center text-xs font-semibold border-r border-slate-500 w-24">
              Auto Depart
            </th>
            <th className="px-2 py-0.5 text-left text-xs font-semibold border-r border-slate-500 w-1/3">
              Preferred Locations
            </th>
            <th className="px-2 py-0.5 text-left text-xs font-semibold border-r border-slate-500 w-1/3">
              Non-Preferred Locations
            </th>
            <th className="px-2 py-0.5 text-left text-xs font-semibold w-1/3">
              Banned Locations
            </th>
          </tr>
          {/* Filter Row */}
          <tr className="bg-slate-500">
            <th className="px-1 py-0.5 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.driver}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, driver: e.target.value }))}
                placeholder="Filter driver"
                className="w-full px-1 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
              />
            </th>
            <th className="px-1 py-0.5 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.startTime}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, startTime: e.target.value }))}
                placeholder="Filter times"
                className="w-full px-1 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
              />
            </th>
            <th className="px-1 py-0.5 border-r border-slate-400">
              <MultiSelect
                options={livePrefOptions}
                value={livePrefFilter}
                onChange={setLivePrefFilter}
                placeholder="All"
                showClearOption={true}
                clearOptionLabel="All"
                enableSearch={false}
                size="sm"
                className="w-full"
              />
            </th>
            <th className="px-1 py-0.5 border-r border-slate-400">
              <MultiSelect
                options={yesNoOptions}
                value={ppeFilter}
                onChange={setPpeFilter}
                placeholder="All"
                showClearOption={true}
                clearOptionLabel="All"
                enableSearch={false}
                size="sm"
                className="w-full"
              />
            </th>
            <th className="px-1 py-0.5 border-r border-slate-400">
              <MultiSelect
                options={yesNoOptions}
                value={prePullFilter}
                onChange={setPrePullFilter}
                placeholder="All"
                showClearOption={true}
                clearOptionLabel="All"
                enableSearch={false}
                size="sm"
                className="w-full"
              />
            </th>
            <th className="px-1 py-0.5 border-r border-slate-400">
              <MultiSelect
                options={yesNoOptions}
                value={autoArrivalFilter}
                onChange={setAutoArrivalFilter}
                placeholder="All"
                showClearOption={true}
                clearOptionLabel="All"
                enableSearch={false}
                size="sm"
                className="w-full"
              />
            </th>
            <th className="px-1 py-0.5 border-r border-slate-400">
              <MultiSelect
                options={yesNoOptions}
                value={autoDepartFilter}
                onChange={setAutoDepartFilter}
                placeholder="All"
                showClearOption={true}
                clearOptionLabel="All"
                enableSearch={false}
                size="sm"
                className="w-full"
              />
            </th>
            <th className="px-1 py-0.5 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.preferred}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, preferred: e.target.value }))}
                placeholder="Filter locations"
                className="w-full px-1 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
              />
            </th>
            <th className="px-1 py-0.5 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.notPreferred}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, notPreferred: e.target.value }))}
                placeholder="Filter locations"
                className="w-full px-1 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
              />
            </th>
            <th className="px-1 py-0.5">
              <input
                type="text"
                value={columnSearches.banned}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, banned: e.target.value }))}
                placeholder="Filter locations"
                className="w-full px-1 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
              />
            </th>
          </tr>
        </thead>
        <tbody className="bg-panel">
          {loading ? (
            <>
              {/* Loading skeleton */}
              {[1, 2, 3].map((groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {/* Dispatcher skeleton row */}
                  <tr className="bg-slate-200 dark:bg-slate-700">
                    <td colSpan={10} className="px-1 py-0.5 border border-rule">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-slate-300 dark:bg-slate-600 rounded animate-pulse"></div>
                        <div className="h-3 w-32 bg-slate-300 dark:bg-slate-600 rounded animate-pulse"></div>
                      </div>
                    </td>
                  </tr>
                  {/* Driver skeleton rows */}
                  {[1, 2, 3].map((driverIndex) => (
                    <tr key={`${groupIndex}-${driverIndex}`}>
                      <td className="px-1 py-0.5 pl-6 border border-rule">
                        <div className="h-3 w-40 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="px-1 py-0.5 border border-rule">
                        <div className="h-3 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="px-1 py-0.5 border border-rule">
                        <div className="h-3 w-20 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="px-1 py-0.5 border border-rule">
                        <div className="h-3 w-12 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="px-1 py-0.5 border border-rule">
                        <div className="h-3 w-32 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="px-1 py-0.5 border border-rule">
                        <div className="h-3 w-32 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="px-1 py-0.5 border border-rule">
                        <div className="h-3 w-32 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {/* Loading indicator overlay */}
              <tr>
                <td colSpan={10} className="px-2 py-1 text-center text-xs text-muted bg-white/80 dark:bg-gray-800/80">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-slate-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading driver preferences...</span>
                  </div>
                </td>
              </tr>
            </>
          ) : filteredData.length === 0 ? (
            <tr>
              <td colSpan={10} className="px-2 py-4 text-center text-xs text-muted">
                No driver preferences data
              </td>
            </tr>
          ) : (
            filteredData.map((group) => {
              const isExpanded = expandedDispatchers.has(group.dispatcher);
              return (
                <React.Fragment key={group.dispatcher}>
                  {/* Dispatcher Row */}
                  <tr
                    className="bg-slate-200 dark:bg-slate-700 cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600"
                    onClick={() => toggleDispatcher(group.dispatcher)}
                  >
                    <td colSpan={10} className="px-1 py-0.5 font-semibold text-xs text-heading border border-rule">
                      <div className="flex items-center gap-1">
                        <ChevronRightIcon
                          className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                        />
                        <span>{group.dispatcher}</span>
                        <span className="text-gray-500 font-normal text-[0.625rem]">
                          ({group.drivers.length})
                        </span>
                      </div>
                    </td>
                  </tr>
                  {/* Driver Rows */}
                  {isExpanded && group.drivers.map((driver) => (
                    <tr key={driver.driver} className="hover:bg-highlight">
                      <td className="px-1 py-0.5 pl-6 text-xs text-gray-700 dark:text-gray-200 border border-rule align-top">
                        <div className="font-medium flex items-center gap-1 flex-wrap">
                          {driver.driver}<MuleBadge isMule={driver.isMule} />
                          {driver.driver_name && (
                            <span className="text-[0.625rem] text-gray-500 dark:text-white">{toTitleCase(driver.driver_name)}</span>
                          )}
                          <DriverPhoneLink textmsgnumber={driver.textmsgnumber} homephone={driver.homephone} />
                          {driver.preference && (
                            <span className={`text-[0.625rem] px-0.5 rounded ${
                              driver.preference === 'OTR'
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                : 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300'
                            }`}>
                              {driver.preference}
                            </span>
                          )}
                          {(() => {
                            const isDarkMode = document.documentElement.classList.contains('dark');
                            const colorStyles = getParkingColorStyles(driver.parking_color, isDarkMode);
                            const defaultParking = !driver.parking_location_name
                              ? getEffectiveDefaultParkingLocation(driver.region, defaultParkingLocationConfig)
                              : null;
                            return (
                              <button
                                onClick={() => openParkingModal(driver)}
                                className={`text-[0.625rem] px-1 py-0 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                                  !colorStyles && (driver.parking_location_name
                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                    : 'bg-gray-100 text-muted dark:bg-gray-700')
                                }`}
                                style={colorStyles || undefined}
                                title={driver.parking_location_name || (defaultParking ? `Default: ${defaultParking.name}` : 'Set parking location')}
                              >
                                {(driver.parking_location_name || (defaultParking ? `Default ${defaultParking.name}` : 'Parking')).slice(0, 12)}{(driver.parking_location_name || '').length > 12 ? '…' : ''}
                              </button>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-1 py-0.5 border border-rule align-top text-center text-xs group">
                        {driver.start_time ? (
                          <div
                            onClick={(e) => {
                              if (deletingStartTime.has(driver.driver)) return;
                              // Check if click came from the remove button - if so, ignore
                              const target = e.target as HTMLElement;
                              if (target.closest('button[type="button"]')) {
                                return;
                              }
                              openStartTimeModal(driver.driver, driver.driver_name, driver.start_time);
                            }}
                            className={`inline-block ${deletingStartTime.has(driver.driver) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <TagBadge
                              label={formatTime12Hour(driver.start_time)}
                              color={deletingStartTime.has(driver.driver) ? 'gray' : 'blue'}
                              className="whitespace-nowrap"
                              onRemove={async () => {
                                // Optimistically remove from UI immediately
                                setData(prevData => prevData.map(group => ({
                                  ...group,
                                  drivers: group.drivers.map(d =>
                                    d.driver === driver.driver ? { ...d, start_time: null } : d
                                  )
                                })));

                                // Process backend delete
                                try {
                                  await api.post('/safety/driver-settings/start-time', {
                                    driver: driver.driver,
                                    start_time: null
                                  });
                                } catch (error) {
                                  console.error('Error clearing start time:', error);
                                  // On error, refresh to restore correct state
                                  handleSaveComplete();
                                }
                              }}
                            />
                          </div>
                        ) : (
                          <button
                            onClick={() => openStartTimeModal(driver.driver, driver.driver_name, driver.start_time)}
                            className="text-gray-400 dark:text-gray-500 text-[0.625rem] cursor-pointer hover:text-gray-600 dark:hover:text-gray-400"
                            title="Click to set custom start time"
                          >
                            Default {formatTime12Hour(getEffectiveDefaultStartTime(driver.region, defaultStartTimeConfig))}
                          </button>
                        )}
                      </td>
                      <td className="px-1 py-0.5 border border-rule align-top text-center">
                        <ThreeStateToggle
                          value={driver.live_pref}
                          onChange={async (value) => {
                            // Optimistically update UI
                            setData(prevData => prevData.map(group => ({
                              ...group,
                              drivers: group.drivers.map(d =>
                                d.driver === driver.driver ? { ...d, live_pref: value } : d
                              )
                            })));

                            // Update backend
                            try {
                              await api.post('/safety/driver-settings/live-pref', {
                                driver: driver.driver,
                                live_pref: value
                              });
                            } catch (error) {
                              console.error('Error updating live preference:', error);
                              // On error, refresh to restore correct state
                              handleSaveComplete();
                            }
                          }}
                          size="xs"
                        />
                      </td>
                      <td className="px-1 py-0.5 border border-rule align-top text-center">
                        <Toggle
                          checked={driver.ppe === 'yes'}
                          onChange={async (checked) => {
                            const newValue = checked ? 'yes' : 'no';
                            // Optimistically update UI
                            setData(prevData => prevData.map(group => ({
                              ...group,
                              drivers: group.drivers.map(d =>
                                d.driver === driver.driver ? { ...d, ppe: newValue } : d
                              )
                            })));

                            // Update backend
                            try {
                              await api.post('/safety/driver-settings/ppe', {
                                driver: driver.driver,
                                ppe: newValue
                              });
                            } catch (error) {
                              console.error('Error updating PPE:', error);
                              // On error, refresh to restore correct state
                              handleSaveComplete();
                            }
                          }}
                          size="xs"
                        />
                      </td>
                      <td className="px-1 py-0.5 border border-rule align-top text-center">
                        <Toggle
                          checked={driver.pre_pull === 'yes'}
                          onChange={async (checked) => {
                            const newValue = checked ? 'yes' : 'no';
                            // Optimistically update UI
                            setData(prevData => prevData.map(group => ({
                              ...group,
                              drivers: group.drivers.map(d =>
                                d.driver === driver.driver ? { ...d, pre_pull: newValue } : d
                              )
                            })));

                            // Update backend
                            try {
                              await api.post('/safety/driver-settings/pre-pull', {
                                driver: driver.driver,
                                pre_pull: newValue
                              });
                            } catch (error) {
                              console.error('Error updating Pre-Pull:', error);
                              // On error, refresh to restore correct state
                              handleSaveComplete();
                            }
                          }}
                          size="xs"
                        />
                      </td>
                      {/* Geofence auto-stamp (#33810). With a switch on, the
                          geofence sweep fills this driver's empty arrival /
                          departure times from GPS instead of waiting for the
                          in-cab app. Arrival and departure are separate because
                          departure agrees with the driver far less often. */}
                      <td className="px-1 py-0.5 border border-rule align-top text-center">
                        <Toggle
                          checked={driver.geo_autostamp_arrival === 'yes'}
                          onChange={async (checked) => {
                            const newValue = checked ? 'yes' : 'no';
                            setData(prevData => prevData.map(group => ({
                              ...group,
                              drivers: group.drivers.map(d =>
                                d.driver === driver.driver ? { ...d, geo_autostamp_arrival: newValue } : d
                              )
                            })));

                            try {
                              await api.post('/safety/driver-settings/geo-autostamp', {
                                driver: driver.driver,
                                geo_autostamp_arrival: newValue
                              });
                            } catch (error) {
                              console.error('Error updating Auto Arrival:', error);
                              handleSaveComplete();
                            }
                          }}
                          size="xs"
                        />
                      </td>
                      <td className="px-1 py-0.5 border border-rule align-top text-center">
                        <Toggle
                          checked={driver.geo_autostamp_depart === 'yes'}
                          onChange={async (checked) => {
                            const newValue = checked ? 'yes' : 'no';
                            setData(prevData => prevData.map(group => ({
                              ...group,
                              drivers: group.drivers.map(d =>
                                d.driver === driver.driver ? { ...d, geo_autostamp_depart: newValue } : d
                              )
                            })));

                            try {
                              await api.post('/safety/driver-settings/geo-autostamp', {
                                driver: driver.driver,
                                geo_autostamp_depart: newValue
                              });
                            } catch (error) {
                              console.error('Error updating Auto Depart:', error);
                              handleSaveComplete();
                            }
                          }}
                          size="xs"
                        />
                      </td>
                      <td className="px-1 py-0.5 border border-rule align-top text-xs group">
                        <div className="flex flex-wrap gap-0.5">
                          {driver.preferred.map((loc, idx) => {
                            const deleteKey = `${driver.driver}-${loc.location_id}-preferred`;
                            const isDeleting = deletingPreferences.has(deleteKey);
                            return (
                              <Tooltip
                                key={idx}
                                content={
                                  <div className="text-xs">
                                    {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                    <div className="font-medium">{loc.location_name}</div>
                                    {loc.location_address1 && (
                                      <div className="mt-1">
                                        <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                        <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                      </div>
                                    )}
                                  </div>
                                }
                                placement="top"
                              >
                                <div className={isDeleting ? 'opacity-50' : ''}>
                                  <TagBadge
                                    label={loc.location_name}
                                    color={isDeleting ? 'gray' : 'green'}
                                    className="whitespace-nowrap"
                                    onRemove={() => !isDeleting && handleDeletePreference(driver.driver, loc.location_id, 'preferred')}
                                  />
                                </div>
                              </Tooltip>
                            );
                          })}
                          <button
                            onClick={() => openPreferenceModal(driver.driver, driver.driver_name, 'preferred')}
                            className="inline-flex items-center px-1 py-0 rounded text-[0.625rem] font-medium bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30 border border-green-200 dark:border-green-800 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Add preferred location"
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td className="px-1 py-0.5 border border-rule align-top text-xs group">
                        <div className="flex flex-wrap gap-0.5">
                          {driver.not_preferred.map((loc, idx) => {
                            const deleteKey = `${driver.driver}-${loc.location_id}-not_preferred`;
                            const isDeleting = deletingPreferences.has(deleteKey);
                            return (
                              <Tooltip
                                key={idx}
                                content={
                                  <div className="text-xs">
                                    {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                    <div className="font-medium">{loc.location_name}</div>
                                    {loc.location_address1 && (
                                      <div className="mt-1">
                                        <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                        <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                      </div>
                                    )}
                                  </div>
                                }
                                placement="top"
                              >
                                <div className={isDeleting ? 'opacity-50' : ''}>
                                  <TagBadge
                                    label={loc.location_name}
                                    color={isDeleting ? 'gray' : 'yellow'}
                                    className="whitespace-nowrap"
                                    onRemove={() => !isDeleting && handleDeletePreference(driver.driver, loc.location_id, 'not_preferred')}
                                  />
                                </div>
                              </Tooltip>
                            );
                          })}
                          <button
                            onClick={() => openPreferenceModal(driver.driver, driver.driver_name, 'not_preferred')}
                            className="inline-flex items-center px-1 py-0 rounded text-[0.625rem] font-medium bg-yellow-50 text-yellow-700 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400 dark:hover:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Add not-preferred location"
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td className="px-1 py-0.5 border border-rule align-top text-xs group">
                        <div className="flex flex-wrap gap-0.5">
                          {driver.banned.map((loc, idx) => {
                            const deleteKey = `${driver.driver}-${loc.location_id}-banned`;
                            const isDeleting = deletingPreferences.has(deleteKey);
                            return (
                              <Tooltip
                                key={idx}
                                content={
                                  <div className="text-xs">
                                    {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                    <div className="font-medium">{loc.location_name}</div>
                                    {loc.location_address1 && (
                                      <div className="mt-1">
                                        <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                        <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                      </div>
                                    )}
                                    <div className="mt-1 font-medium text-red-400">
                                      {formatBanPeriod(loc)}
                                    </div>
                                  </div>
                                }
                                placement="top"
                              >
                                <div
                                  onClick={(e) => !isDeleting && handleEditBan(driver.driver, driver.driver_name, loc, e)}
                                  className={isDeleting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                >
                                  <TagBadge
                                    label={`${loc.location_name} - ${formatBanPeriod(loc)}`}
                                    color={isDeleting ? 'gray' : 'red'}
                                    className="whitespace-nowrap"
                                    onRemove={() => !isDeleting && handleDeletePreference(driver.driver, loc.location_id, 'banned')}
                                  />
                                </div>
                              </Tooltip>
                            );
                          })}
                          <button
                            onClick={() => openPreferenceModal(driver.driver, driver.driver_name, 'banned')}
                            className="inline-flex items-center px-1 py-0 rounded text-[0.625rem] font-medium bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Add banned location"
                          >
                            +
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>

      {/* Preference Modal */}
      <DriverPreferenceModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingBan(null);
        }}
        driver={modalDriver}
        driverName={modalDriverName}
        preferenceType={modalPreferenceType}
        editingBan={editingBan}
        onSave={handleSaveComplete}
      />

      {/* Delete Confirmation Modal */}
      <InfoModal
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTarget(null);
        }}
        title="Confirm Delete"
        maxWidth="sm"
        body={
          <div className="text-sm text-label">
            Are you sure you want to remove this {deleteTarget?.preferenceType.replace('_', '-')} location?
          </div>
        }
        buttons={[
          {
            label: 'Cancel',
            onClick: () => {
              setConfirmOpen(false);
              setDeleteTarget(null);
            },
            variant: 'secondary'
          },
          {
            label: 'Delete',
            onClick: confirmDelete,
            variant: 'primary'
          }
        ]}
      />

      {/* Start Time Modal */}
      <InfoModal
        isOpen={startTimeModalOpen}
        onClose={() => setStartTimeModalOpen(false)}
        title={`Set Start Time - ${startTimeDriver} ${toTitleCase(startTimeDriverName)}`}
        maxWidth="sm"
        body={
          <div className="space-y-4">
            <DateTimePicker
              mode="time"
              selectionType="single"
              value={(() => {
                if (startTimeValue) {
                  const [hours, minutes] = startTimeValue.split(':');
                  const date = new Date();
                  date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                  return date;
                }
                return null;
              })()}
              onChange={handleStartTimeChange}
              placeholder="Select start time"
              labelType="custom"
              label="Preferred Start Time"
              openDirection="down"
              usePortal={true}
            />
          </div>
        }
        buttons={[
          {
            label: 'Cancel',
            onClick: () => setStartTimeModalOpen(false),
            variant: 'secondary'
          },
          {
            label: savingStartTime ? 'Saving...' : 'Save',
            onClick: handleSaveStartTime,
            variant: 'primary',
            disabled: savingStartTime
          }
        ]}
      />

      {/* Parking Location Modal */}
      <ParkingLocationModal
        isOpen={parkingModalOpen}
        onClose={() => setParkingModalOpen(false)}
        driver={parkingModalDriver}
        driverName={parkingModalDriverName}
        currentParkingloc={parkingModalCurrentLoc}
        currentParkingName={parkingModalCurrentName}
        currentParkingColor={parkingModalCurrentColor}
        onSave={handleParkingLocationSave}
      />
    </div>
  );
};

export default DriverPreferencesBans;
